import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TrialBalanceTab = ({ getAuthHeader, asOfDate }) => {
  const [loading, setLoading] = useState(false);
  const [trialBalance, setTrialBalance] = useState(null);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const getAccountTypeColor = (type) => {
    switch (type) {
      case 'asset': return 'bg-blue-100 text-blue-800';
      case 'liability': return 'bg-red-100 text-red-800';
      case 'equity': return 'bg-purple-100 text-purple-800';
      case 'income': return 'bg-green-100 text-green-800';
      case 'expense': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const fetchTrialBalance = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/accounting/reports/trial-balance?as_of_date=${asOfDate}`,
        { headers: getAuthHeader() }
      );
      setTrialBalance(res.data);
    } catch (error) {
      toast.error('Failed to fetch trial balance');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrialBalance();
  }, [asOfDate]);

  const handleExportPDF = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/accounting/reports/trial-balance/pdf?as_of_date=${asOfDate}`,
        { headers: getAuthHeader(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `trial_balance_${asOfDate}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF exported!');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/accounting/reports/trial-balance/excel?as_of_date=${asOfDate}`,
        { headers: getAuthHeader(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `trial_balance_${asOfDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel exported!');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Trial Balance</CardTitle>
          <CardDescription>As of {asOfDate}</CardDescription>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportPDF} data-testid="export-tb-pdf">
            <Download className="h-4 w-4 mr-2" />
            PDF
          </Button>
          <Button variant="outline" onClick={handleExportExcel} data-testid="export-tb-excel">
            <Download className="h-4 w-4 mr-2" />
            Excel
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : trialBalance ? (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Account Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Debit</TableHead>
                  <TableHead className="text-right">Credit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {trialBalance.accounts?.map((acc) => (
                  <TableRow key={acc.account_code}>
                    <TableCell className="font-mono">{acc.account_code}</TableCell>
                    <TableCell>{acc.account_name}</TableCell>
                    <TableCell>
                      <Badge className={getAccountTypeColor(acc.account_type)}>
                        {acc.account_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {acc.debit > 0 ? formatCurrency(acc.debit) : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      {acc.credit > 0 ? formatCurrency(acc.credit) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="font-bold bg-muted">
                  <TableCell colSpan={3}>Total</TableCell>
                  <TableCell className="text-right">{formatCurrency(trialBalance.total_debit)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(trialBalance.total_credit)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
            
            <div className={`mt-4 p-3 rounded-lg flex items-center gap-2 ${
              trialBalance.is_balanced ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {trialBalance.is_balanced ? '✓ Trial Balance is balanced' : '✗ Trial Balance is NOT balanced'}
            </div>
          </>
        ) : (
          <p className="text-center py-8 text-muted-foreground">No data available</p>
        )}
      </CardContent>
    </Card>
  );
};

export default TrialBalanceTab;
